import { useState } from 'react';
import type { LlmConfig } from '@/types';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface LlmConfigListProps {
  configs: LlmConfig[];
  onEdit: (config: LlmConfig) => void;
  onDelete: (serviceId: string) => void;
  isDeleting?: boolean;
}

export function LlmConfigList({ configs, onEdit, onDelete, isDeleting }: LlmConfigListProps) {
  const [deleteTarget, setDeleteTarget] = useState<LlmConfig | null>(null);

  function handleDeleteConfirm() {
    if (deleteTarget) {
      onDelete(deleteTarget.serviceId);
      setDeleteTarget(null);
    }
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Service ID</TableHead>
            <TableHead>Provider</TableHead>
            <TableHead>Model</TableHead>
            <TableHead>API Key</TableHead>
            <TableHead>Temperature</TableHead>
            <TableHead>Max Tokens</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {configs.map((config) => (
            <TableRow key={config.serviceId}>
              <TableCell className="font-medium">{config.serviceId}</TableCell>
              <TableCell>{config.providerType}</TableCell>
              <TableCell>{config.modelName}</TableCell>
              <TableCell className="font-mono text-muted-foreground">
                {config.maskedApiKey ?? '***'}
              </TableCell>
              <TableCell>{config.temperature}</TableCell>
              <TableCell>{config.maxTokenLimit}</TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(config)}>
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeleteTarget(config)}
                  disabled={isDeleting}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete LLM Configuration</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the configuration for service{' '}
              <span className="font-semibold">{deleteTarget?.serviceId}</span>? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
